// Shared overhead-category rules for the time loggers (QuickTimeLog + My Time).
//
// "Other" is the category of last resort. The Time Logging How To Guide makes
// the note compulsory against it, for a reason anybody who has read a timesheet
// back will recognise: an hour filed as "Other" with nothing written on it
// cannot be explained later by anybody, including the person who logged it.
//
// This lived inline in the quick panel, so the full Add Entry form let the same
// entry through and which screen you happened to open decided whether the rule
// applied. It is here so both screens ask the same question and get the same
// answer.

/** Shown when an "Other" entry has no note. Both loggers use this wording. */
export const OTHER_NOTE_MESSAGE =
  'Please say what this was — notes are required when the category is Other.';

/**
 * Is this category an "Other" bucket?
 *
 * Matched on the name of the category or of its parent, because "Other" exists
 * at both levels ("Other", and "Other > Other") and neither carries a code to
 * match on. A word-boundary match, so a category that merely contains the
 * letters (a supplier called "Brother", say) is not swept up.
 *
 * Ids are compared as strings: a <select> always holds its value as one, while
 * the API returns numbers for some tables, and === on the mixed pair silently
 * never fires.
 *
 * An id that is empty, or not in the list, is not Other. The list can still be
 * loading, and refusing to save against a category we cannot see would block a
 * legitimate entry.
 *
 * @param {Array<{id: string|number, name?: string, parent_id?: string|number|null}>} categories
 * @param {string|number|null|undefined} id
 * @returns {boolean}
 */
export function isOtherOverhead(categories, id) {
  if (!id || !Array.isArray(categories) || categories.length === 0) return false;
  const leaf = categories.find(c => String(c.id) === String(id));
  if (!leaf) return false;
  const parent = leaf.parent_id != null
    ? categories.find(c => String(c.id) === String(leaf.parent_id))
    : null;
  return /(^|\b)other(\b|$)/i.test(`${leaf.name || ''} ${parent?.name || ''}`);
}

export default isOtherOverhead;
